import React, { useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';

import { Menu, Item, Submenu } from 'react-contexify';

import 'react-contexify/dist/ReactContexify.css';
import PropTypes, { shape } from 'prop-types';

const StyledMenu = styled(Menu)(({ theme }) => ({
  '--contexify-menu-padding': '8px 0',
  '--contexify-menu-radius': '4px',
  '--contexify-menu-bgColor': theme.palette.background.paper,
  '--contexify-menu-shadow': theme.shadows[3],
  '--contexify-menu-minWidth': '220px',
  '--contexify-activeItem-radius': '0',

  // Item styles
  '--contexify-item-padding': '6px 16px',
  '--contexify-item-color': theme?.palette?.primary?.main,
  '--contexify-activeItem-color': theme?.palette?.primary?.main,
  '--contexify-activeItem-bgColor': theme.palette.action.hover,

  // Submenu arrow
  '--contexify-arrow-color': theme?.palette?.primary?.main,
  '--contexify-activeArrow-color': theme?.palette?.primary?.main,
  '--contexify-arrow-size': '0.8em',
  '--contexify-arrow-margin': '8px',

  // Separator
  '--contexify-separator-color': theme.palette.divider,
  '--contexify-separator-margin': '6px 14px',

  // Disabled state
  '--contexify-itemDisabled-color': theme.palette.text.disabled,
  '--contexify-itemDisabled-bgColor': 'transparent',

  // Custom styles to match MUI
  '& .contexify_itemContent': {
    fontSize: theme.typography.body1.fontSize,
    fontFamily: theme.typography.fontFamily,
  },

  '& .contexify_separator': {
    margin: '6px 0',
  },

  '& .contexify_item': {
    position: 'relative',
    transition: theme.transitions.create(['background-color', 'color']),

    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      cursor: 'pointer',
    },

    '&[aria-disabled="true"]': {
      opacity: theme.palette.action.disabledOpacity,
      pointerEvents: 'none',
    },
  },
}));

const RecursiveMenu = ({
  menus = [],
  menuId,
  onMenuClick,
  isSubMenu = false,
  subMenuDetails = null,
}) => {
  const theme = useTheme();
  return isSubMenu ? (
    <Submenu
      label={
        <Box display="flex" alignItems="center">
          {subMenuDetails.icon ? <subMenuDetails.icon color="primary" /> : null}
          <Box ml={2} display="flex" flexGrow={1}>
            <Typography>{subMenuDetails?.label}</Typography>
          </Box>
        </Box>
      }
      disabled={subMenuDetails?.isMenuDisabled}
    >
      {menus.map((menu) =>
        menu.subMenus?.length ? (
          <RecursiveMenu
            menus={menu.subMenu}
            isSubMenu={true}
            onMenuClick
            menuId
            key={`${menu.id}_${menu.label}`}
          />
        ) : (
          <Item
            id={menu.id}
            onClick={onMenuClick}
            key={`${menu.id}_${menu.label}`}
            data={menu}
            disabled={menu.isMenuDisabled}
          >
            <Box display="flex" alignItems="center">
              {menu?.icon ? <menu.icon color="primary" /> : null}
              <Box ml={2} display="flex" flexGrow={1}>
                <Typography>{menu.label}</Typography>
              </Box>
            </Box>
          </Item>
        )
      )}
    </Submenu>
  ) : (
    <StyledMenu id={menuId}>
      {menus.map((menu) =>
        menu.subMenus?.length ? (
          <RecursiveMenu
            menus={menu.subMenus}
            isSubMenu={true}
            subMenuDetails={menu}
            onMenuClick={onMenuClick}
            menuId={menuId}
            key={`${menu.id}_${menu.label}`}
          />
        ) : (
          <Item
            id={menu.id}
            onClick={onMenuClick}
            data={menu}
            key={`${menu.id}_${menu.label}`}
            disabled={menu.isMenuDisabled}
          >
            <Box display="flex" alignItems="center">
              {menu?.icon ? (
                <menu.icon color="primary" width={21} height={21} />
              ) : null}
              <Box ml={2} display="flex" flexGrow={1}>
                <Typography
                  color={theme.palette?.primary?.main}
                  variant="body1"
                >
                  {menu.label}
                </Typography>
              </Box>
            </Box>
          </Item>
        )
      )}
    </StyledMenu>
  );
};

export default RecursiveMenu;

RecursiveMenu.propTypes = {
  menus: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      id: PropTypes.string.isRequired,
      icon: PropTypes.element.isRequired,
      onClick: PropTypes.func.isRequired,
    })
  ).isRequired,
  menuId: PropTypes.string.isRequired,
  onMenuClick: PropTypes.func.isRequired,
  isSubMenu: PropTypes.bool.isRequired,
  subMenuDetails: PropTypes.shape({}).isRequired,
  additionalData: PropTypes.shape({}).isRequired,
};

export const ContextMenus = ({
  menus = [],
  additionalData = {},
  handleMenuClick,
  menuId,
}) => {
  const handleItemClick = useCallback(
    (menuClickEventData) => {
      if (handleMenuClick) {
        handleMenuClick(menuClickEventData);
      } else {
        menuClickEventData.data?.onClick(menuClickEventData);
      }
    },
    [handleMenuClick]
  );

  return (
    <RecursiveMenu
      menus={menus}
      menuId={menuId}
      isSubMenu={false}
      onMenuClick={handleItemClick}
      additionalData={additionalData}
    />
  );
};

ContextMenus.propTypes = {
  menus: PropTypes.arrayOf(
    shape({
      label: PropTypes.string.isRequired,
      id: PropTypes.string.isRequired,
      icon: PropTypes.element.isRequired,
      onClick: PropTypes.func.isRequired,
    })
  ).isRequired,
  additionalData: PropTypes.shape({}).isRequired,
  handleMenuClick: PropTypes.func.isRequired,
  menuId: PropTypes.string.isRequired,
};
